<template>
  <v-container>
    <h2>Gruppenanmeldung</h2>
    <p>Hier könnt ihr mehrere Teilnehmer*innen auf einmal anmelden.</p>
    <p>Bitte benutzt dieses Formular nur, wenn ihr eine Spendenrechnung braucht.</p>
    <p>Bitte beachtet, dass die eventuell bestellten T-Shirts alle in einem Paket an eine Adresse versendet werden.</p>
    <v-form
        ref="form"
        lazy-validation
    >
      <TeamInfoCard
          :team.sync="team"
          :contact-email.sync="contactEmail"
          :amount.sync="amount"
          :amount-items="amountItems"
      />

      <ParticipantsCard
          :participants.sync="participants"
      />

      <ShirtsCard
          :want-shirts.sync="wantShirts"
          :shirts.sync="shirts"
          :package-fee.sync="packageFee"
          :package-fee-items="packageFeeItems"
          :shirts-enabled="$store.state.shirtsEnabled"
          @packageFeeChanged="onPackageFeeChanged"
      />

      <AddressCard
          :address.sync="address"
      />

      <SummaryCard
          :participant-count="participants.length"
          :amount="amount"
          :want-shirts="wantShirts"
          :shirt-count="shirts.length"
          :package-fee="packageFee"
      />

      <v-checkbox
          v-model="agbCheckbox"
          :rules="[v => !!v || 'Muss leider gelesen werden']"
          required
      >
        <template v-slot:label>
          <div>
            Mir ist bewusst, dass die Datenverarbeitung entsprechend der
            <a target="_blank" href="/#/datenschutz" @click.stop>Datenschutzbestimmungen</a>
            der Webseite lauf-gegen-rechts.de erfolgt.
          </div>
        </template>
      </v-checkbox>

      <v-btn
          color="brown"
          class="white--text mr-4"
          @click.stop="register"
          :loading="loading"
      >
        Gruppe anmelden
      </v-btn>

      <SuccessDialog
          :show="registrationSuccessful"
          :result="registrationResult"
          @close="clearFields"
      />

      <ErrorDialog
          :show="showError"
          :message="errorMessage"
          @close="showError = false"
      />
    </v-form>
  </v-container>
</template>

<script>
import axios from 'axios'
import TeamInfoCard from '@/components/GroupRegistration/TeamInfoCard.vue'
import ParticipantsCard from '@/components/GroupRegistration/ParticipantsCard.vue'
import ShirtsCard from '@/components/GroupRegistration/ShirtsCard.vue'
import AddressCard from '@/components/GroupRegistration/AddressCard.vue'
import SummaryCard from '@/components/GroupRegistration/SummaryCard.vue'
import SuccessDialog from '@/components/GroupRegistration/SuccessDialog.vue'
import ErrorDialog from '@/components/GroupRegistration/ErrorDialog.vue'

export default {
  name: 'GroupRegistration',
  components: {
    TeamInfoCard,
    ParticipantsCard,
    ShirtsCard,
    AddressCard,
    SummaryCard,
    SuccessDialog,
    ErrorDialog
  },
  data: () => ({
    team: '',
    contactEmail: '',
    amount: { amount: 10, name: 'normal', label: '10€ (normal)' },
    amountItems: [
      { amount: 5, name: 'cheap', label: '5€ (ermäßigt)' },
      { amount: 10, name: 'normal', label: '10€ (normal)' },
      { amount: 15, name: 'more', label: '15€ (großzügig)' },
      { amount: 20, name: 'much', label: '20€ (sehr großzügig)' },
    ],
    participants: [{ firstName: '' }],
    wantShirts: false,
    shirts: [],
    address: {
      companyName: '',
      firstname: '',
      lastname: '',
      street: '',
      streetNumber: '',
      extra: '',
      city: '',
      plz: '',
      country: 'Deutschland',
    },
    packageFee: { amount: 0, name: 'innerhalb Deutschlands (versandkostenfrei)' },
    packageFeeItems: [
      { amount: 0, name: 'innerhalb Deutschlands (versandkostenfrei)' },
      { amount: 2, name: 'EU (+2€ Versand)' },
      { amount: 5, name: 'non-EU (+5€ Versand)' },
    ],
    agbCheckbox: false,
    registrationSuccessful: false,
    registrationResult: null,
    loading: false,
    showError: false,
    errorMessage: '',
  }),
  methods: {
    onPackageFeeChanged(value) {
      if (value.amount === 0) {
        this.address = { ...this.address, country: 'Deutschland' };
      } else {
        this.address = { ...this.address, country: '' };
      }
    },
    async register() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // Additional validation
      const validParticipants = this.participants.filter(p => p.firstName.trim());
      if (validParticipants.length === 0) {
        this.errorMessage = 'Mindestens ein*e Teilnehmer*in ist erforderlich.';
        this.showError = true;
        return;
      }

      if (this.wantShirts && this.shirts.length > 0) {
        const invalidShirts = this.shirts.filter(s => !s.model || !s.size);
        if (invalidShirts.length > 0) {
          this.errorMessage = 'Bitte Model und Größe für alle T-Shirts auswählen.';
          this.showError = true;
          return;
        }
      }

      this.loading = true;

      const data = {
        team: this.team,
        contactEmail: this.contactEmail || undefined,
        amount: this.amount.name,
        participants: validParticipants.map(p => ({
          firstName: p.firstName.trim(),
          lastName: '', // Backend requires lastName but we only collect firstName
        })),
        address: {
          companyName: this.address.companyName || undefined,
          firstname: this.address.firstname,
          lastname: this.address.lastname,
          street: this.address.street,
          streetNumber: this.address.streetNumber,
          city: this.address.city,
          plz: this.address.plz,
          country: this.address.country,
          extra: this.address.extra || undefined,
          packageFee: this.wantShirts && this.shirts.length > 0 ? this.packageFee.amount : 0,
        },
      };

      if (this.wantShirts && this.shirts.length > 0) {
        data.shirts = this.shirts.map(s => ({
          model: s.model,
          size: s.size,
        }));
      }

      const url = `${this.$base_url}/participant/register-bulk`;

      try {
        const response = await axios.put(url, data);
        this.registrationResult = response.data;
        this.registrationSuccessful = true;
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Ein Fehler ist aufgetreten.';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },
    clearFields() {
      this.$refs.form.reset();
      this.registrationSuccessful = false;
      this.participants = [{ firstName: '' }];
      this.shirts = [];
      this.wantShirts = false;
      this.address = {
        companyName: '',
        firstname: '',
        lastname: '',
        street: '',
        streetNumber: '',
        extra: '',
        city: '',
        plz: '',
        country: 'Deutschland',
      };
      this.packageFee = { amount: 0, name: 'innerhalb Deutschlands (versandkostenfrei)' };
      this.amount = { amount: 10, name: 'normal', label: '10€ (normal)' };
    },
  },
}
</script>
